import styled from '@emotion/styled';
import { Button, message } from 'antd';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import { useAtomValue } from 'jotai';
import copyText from 'utils/copyText';
import TradeProduct from 'views/Conversation/Sider/TradeProduct';
import { useTradeContext } from 'views/Conversation/Sider/StateContext';
import ImageSlide from './ImageSlide';
import { StarRate } from './Profile';
import { SystemMessage } from './ReplyDetail';
import { BetweenStart, Operation, ReviewMessage } from './BuyerReviewDetail';
import Translation from './Translation';
import { useGlobalReview } from '../hooks/useNewReview';
import { useReviewContext } from '../Context';
import { currentReviewProfileState } from '../hooks/useReview';
import { Fragment, useMemo } from 'react';
import { map } from 'lodash-es';
import { filter } from 'lodash-es';
import { ProductTagList, TagProduct } from 'beisi/TagProduct';
import { useTranslateReport } from 'hooks/useTranslateReport';

const RateWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  dd {
    margin-bottom: 0;
  }
  dl {
    margin-bottom: 5px;
    margin-right: 24px;
  }
  dt {
    margin-top: 5px;
    color: #515359;
  }
`;

const UIProduct = styled(TradeProduct)`
  border-bottom: 1px solid #efefef;
  margin-bottom: 8px;
  padding: 8px 0;
`;

export interface LazadaReviewDetailProps {
  reviewDetail: Review.ReviewDetail;
  isMainReview?: boolean;
}

function LazadaReviewDetail(props: LazadaReviewDetailProps) {
  const { reviewDetail, isMainReview = false } = props;

  const translateReport = useTranslateReport();

  const {
    id,
    commentCreateTime,
    ratingStar,
    productRating,
    sellerRating,
    logisticsRating,
    buyerComment,
    transBuyerComment,
  } = reviewDetail;

  const { t } = useTranslate();

  const { changeFiltePartial } = useGlobalReview();

  const { conversation } = useReviewContext();

  const [trade] = useTradeContext();

  const currentReview = useAtomValue(currentReviewProfileState);

  const rate = (
    <RateWrapper>
      <dl>
        <dt>{t('domain.review.field.overallRating')}</dt>
        <dd>
          <StarRate allowHalf disabled count={5} value={ratingStar} />
        </dd>
      </dl>
      <dl>
        <dt>{t('domain.review.field.productRating')}</dt>
        <dd>
          <StarRate allowHalf disabled count={5} value={productRating} />
        </dd>
      </dl>
      <dl>
        <dt>{t('domain.review.field.logisticsRating')}</dt>
        <dd>
          <StarRate allowHalf disabled count={5} value={logisticsRating} />
        </dd>
      </dl>
      <dl>
        <dt>{t('domain.review.field.sellerRating')}</dt>
        <dd>
          <StarRate allowHalf disabled count={5} value={sellerRating} />
        </dd>
      </dl>
    </RateWrapper>
  );

  const [commentProducts, spuName] = useMemo(() => {
    let spuName = '';
    const match = map(trade?.items, (item) => {
      const { itemComments, itemName } = item;
      const hasThisComment = itemComments?.some((v) => {
        if (v.commentId === currentReview?.commentId) {
          spuName = itemName;
          return true;
        }
        return false;
      });
      return hasThisComment ? item : undefined!;
    });
    return [filter(match, Boolean), spuName] as const;
  }, [trade, currentReview]);

  const ComentProducts = commentProducts?.map((item, index) => {
    return (
      <Fragment key={`${item.itemId}-${index}`}>
        <UIProduct
          product={item}
          conversation={conversation}
          productIndex={index}
          showReturn
          showComment={false}
          externalShopId={trade.externalShopId}
        />
        <ProductTagList productData={item} trade={trade} />
      </Fragment>
    );
  });

  const UIFindProduct = spuName ? (
    <Button type="link" onClick={() => changeFiltePartial({ spuName })}>
      {t('scene.reviewWorkspace.list.action.filterByProduct')}
    </Button>
  ) : null;

  return (
    <>
      <SystemMessage>
        {`${dayjs(parseInt(commentCreateTime, 10)).format('YYYY-MM-DD HH:mm:ss')} ${t(
          'scene.reviewWorkspace.detail.status.userPostedReview',
        )}`}
      </SystemMessage>
      <ReviewMessage>
        {buyerComment ? (
          <Translation
            originText={buyerComment}
            translationMap={transBuyerComment}
            afterTranslation={(lang, text) => {
              translateReport(
                {
                  id,
                  flag: isMainReview ? 1 : 2,
                  transComment: {
                    [lang]: text,
                  },
                },
                'Review',
              );
            }}
          >
            {({ text, operation, displayTranslation }) => {
              return (
                <>
                  {ComentProducts}
                  <TagProduct
                    productData={commentProducts}
                    border={['bottom']}
                    trade={trade}
                    className="pb-[8px]"
                    conversation={conversation}
                    sourceId={reviewDetail.id}
                  />
                  <BetweenStart>
                    {rate}
                    <Operation>
                      {UIFindProduct}
                      {displayTranslation && (
                        <Button
                          type="link"
                          onClick={() => {
                            copyText(text)
                              .then(() => {
                                message.success(t('common.feedback.copySuccess'));
                              })
                              .catch(() => {
                                message.error(t('common.feedback.copyFailed'));
                              });
                          }}
                        >
                          {t('scene.reviewWorkspace.detail.action.copyReview')}
                        </Button>
                      )}
                      {operation}
                    </Operation>
                  </BetweenStart>
                  <div>{text}</div>
                </>
              );
            }}
          </Translation>
        ) : (
          <>
            {ComentProducts}
            <BetweenStart>
              {rate}
              <Operation>{UIFindProduct}</Operation>
            </BetweenStart>
          </>
        )}
        <ImageSlide reviewDetail={reviewDetail} />
      </ReviewMessage>
    </>
  );
}

export default LazadaReviewDetail;
